<template>
  <div
    class="form-group"
    :class="wrapperClass"
  >
    <label
      v-if="hasLabel"
      :class="labelClass"
      :for="cmpId"
    >
      {{ label }}
      <!-- @slot Slot for additional content in label, you can put here for example info icon with tooltip -->
      <slot/>
    </label>

    <template v-if="(hasInputPrepend || hasInputAppend) && !isStatic">
      <div
        class="input-group"
        :class="inputGroupClasses"
      >
        <div
          v-if="hasInputPrepend"
          ref="input-prepend"
          class="input-group-prepend"
        >
          <!-- @slot Input group prepend -->
          <slot name="input-prepend"/>
        </div>

        <input
          v-bind="inputAttributes"
        >

        <div
          v-if="hasInputAppend"
          ref="input-append"
          class="input-group-append"
        >
          <!-- @slot Input group append -->
          <slot name="input-append"/>
        </div>
      </div>
    </template>

    <template v-if="!hasInputPrepend && !hasInputAppend && !isStatic">
      <input
        v-bind="inputAttributes"
      >
    </template>

    <p
      v-if="isStatic"
      class="form-control-plaintext"
    >
      {{ modelValue }}
    </p>

    <div
      v-if="helpBlock"
      :id="ariaDescribedBy"
      class="form-text small"
    >
      {{ helpText }}
    </div>

    <!-- @slot Space for overriding custom validation feedback -->
    <slot name="validationFeedback">
      <div
        v-if="validationFeedbackText"
        :class="validationFeedbackClass"
      >
        {{ validationFeedbackText }}
      </div>
    </slot>
  </div>
</template>

<script>
import { SIZE_LG } from '../../config/constants';
import formControl from '../../mixins/form-control';
import formControlText from '../../mixins/form-control-text';
import {
  INPUT_GROUP_STYLE_DEFAULT,
  INPUT_GROUP_STYLE_TRANSPARENT,
  INPUT_GROUP_STYLES,
  INPUT_TYPE_TEXT,
  INPUT_TYPES,
} from './config';

const INPUT_PREPEND = 'input-prepend';
const INPUT_APPEND = 'input-append';

export default {
  name: 'DpInput',

  mixins: [formControlText, formControl],

  props: {
    formControlClass: {
      type: [String, Array, Object],
      required: false,
      default: '',
    },
    /**
     * @values default, transparent
     * @default default
     * */
    styleInputGroup: {
      type: String,
      default: INPUT_GROUP_STYLE_DEFAULT,
      required: false,
      validator: (style) => INPUT_GROUP_STYLES.includes(style),
    },
    /**
     * @values text, password, email, etc.
     * @default text
     * */
    type: {
      type: String,
      required: false,
      default: INPUT_TYPE_TEXT,
      validator: (value) => INPUT_TYPES.includes(value),
    },
  },

  emits: [
    'update:modelValue',
  ],

  data: () => ({
    hasInputPrepend: true,
    hasInputAppend: true,
  }),

  computed: {
    inputGroupClasses() {
      const classes = [];

      if (this.styleInputGroup === INPUT_GROUP_STYLE_TRANSPARENT) {
        classes.push(`input-group-${INPUT_GROUP_STYLE_TRANSPARENT}`);
      }

      if (this.size === SIZE_LG) {
        classes.push('input-group-lg');
      }

      return classes;
    },

    inputAttributes() {
      const definedAttributes = {
        'aria-describedby': this.ariaDescribedBy,
        id: this.cmpId,
        class: this.formControlClasses,
        disabled: this.disabled,
        placeholder: this.placeholder,
        readonly: this.readonly,
        type: this.type,
        value: this.modelValue,
      };

      return {
        ...this.inputAttrs,
        ...definedAttributes,
      };
    },
  },

  mounted() {
    this.checkInputPrepend();
    this.checkInputAppend();
  },

  methods: {
    input(event) {
      /**
       * Emits input value
       *
       * @type {text}
       */
      this.$emit('update:modelValue', event.target.value);
    },

    checkInputExtras(extrasType) {
      const extrasEL = this.$refs[extrasType];

      if (!extrasEL) {
        return false;
      }

      return extrasEL.children.length > 0;
    },

    checkInputPrepend() {
      this.hasInputPrepend = this.checkInputExtras(INPUT_PREPEND);
    },

    checkInputAppend() {
      this.hasInputAppend = this.checkInputExtras(INPUT_APPEND);
    },
  },
};
</script>
